/* =============================================================================
   bogo.js  —  Generic "Buy X Get Y" auto-gift engine for Shopify (theme code)
   -----------------------------------------------------------------------------
   Campaigns come from window.BOGO_CAMPAIGNS (bogo-config.liquid).

   Behaviour:
   - Popup opens AUTOMATICALLY once the cart holds enough trigger items
     (e.g. 2 sleeves). Never opens on the product page before adding to cart.
   - Gift picker uses QUANTITY steppers; chosen quantities must total the number
     of free items currently owed.
   - Scales with sets when repeat=true; capped at gift_qty when repeat=false.
   - Tops up if they qualify for more; trims extras if they drop below.
   - Respects "No thanks": won't nag unless they qualify for MORE than declined.
   - Shopify's native automatic Buy X Get Y discount makes the gifts free; keep
     its products/quantities (and max-uses-per-order) in sync with the config.
   ============================================================================= */

(function () {
  'use strict';

  var CAMPAIGNS = window.BOGO_CAMPAIGNS || [];
  if (!Array.isArray(CAMPAIGNS) || CAMPAIGNS.length === 0) return;

  var _giftCache = {};
  var _syncing = false;

  // ---- dismiss memory (per session) ----------------------------------------
  function getDismissed(id) {
    try { var v = sessionStorage.getItem('bogo_dismiss_' + id); return v === null ? -1 : Number(v); }
    catch (e) { return -1; }
  }
  function setDismissed(id, val) { try { sessionStorage.setItem('bogo_dismiss_' + id, String(val)); } catch (e) {} }

  // --------------------------------------------------------------------------
  // Data helpers
  // --------------------------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  function fetchGiftProducts(campaign) {
    if (_giftCache[campaign.id]) return Promise.resolve(_giftCache[campaign.id]);
    var handles = campaign.gift_products || [];
    return Promise.all(handles.map(function (h) {
      return fetch('/products/' + h + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    })).then(function (list) {
      var products = list.filter(Boolean);
      _giftCache[campaign.id] = products;
      return products;
    });
  }

  function triggerQtyInCart(cart, campaign) {
    var triggers = campaign.trigger_products || [];
    return cart.items.reduce(function (sum, item) {
      return triggers.indexOf(item.handle) !== -1 ? sum + item.quantity : sum;
    }, 0);
  }

  function giftLinesFor(cart, campaign) {
    return cart.items.filter(function (item) {
      return item.properties && item.properties._bogo_campaign === campaign.id;
    });
  }
  function giftQtyInCart(cart, campaign) {
    return giftLinesFor(cart, campaign).reduce(function (s, l) { return s + l.quantity; }, 0);
  }

  // How many free items are OWED right now, given trigger quantity + repeat.
  function targetGiftQty(cart, campaign) {
    var X = Number(campaign.trigger_qty), Y = Number(campaign.gift_qty);
    var triggerQty = triggerQtyInCart(cart, campaign);
    if (triggerQty < X) return 0;
    var repeat = campaign.repeat !== false; // default true
    var sets = repeat ? Math.floor(triggerQty / X) : 1;
    return sets * Y;
  }

  // --------------------------------------------------------------------------
  // Popup with QUANTITY steppers
  // --------------------------------------------------------------------------
  function getPopup() { return document.getElementById('bogo-offer-popup'); }

  function renderPopup(campaign, giftProducts, pickCount, target) {
    var popup = getPopup();
    if (!popup) return;
    var list = popup.querySelector('.bogo-gift-list');

    var available = giftProducts.filter(function (p) {
      return p && p.variants && p.variants[0] && p.variants[0].available !== false;
    });
    if (available.length === 0 || pickCount <= 0) return;

    list.innerHTML = available.map(function (p) {
      var v = p.variants[0];
      var img = p.featured_image || (p.images && p.images[0]) || '';
      if (img && typeof img === 'object') img = img.src;
      return (
        '<div class="bogo-gift-card" data-variant="' + v.id + '" data-qty="0" data-name="' + escapeHtml(p.title) + '">' +
          (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
          '<span class="bogo-gift-card__title">' + escapeHtml(p.title) + '</span>' +
          '<span class="bogo-gift-card__price">Free</span>' +
          '<div class="bogo-qty">' +
            '<button type="button" class="bogo-qty__btn" data-dir="-1" aria-label="Decrease">&minus;</button>' +
            '<span class="bogo-qty__val">0</span>' +
            '<button type="button" class="bogo-qty__btn" data-dir="1" aria-label="Increase">+</button>' +
          '</div>' +
        '</div>'
      );
    }).join('');

    popup.querySelector('.bogo-title').textContent = campaign.title || 'Choose your free gift';
    popup.querySelector('.bogo-need').textContent = pickCount;
    popup.querySelector('.bogo-picked').textContent = '0';
    popup.dataset.campaignId = campaign.id;
    popup.dataset.pickCount = pickCount;
    popup.dataset.target = target;

    list.onclick = function (e) {
      var btn = e.target.closest('.bogo-qty__btn');
      if (!btn) return;
      var card = btn.closest('.bogo-gift-card');
      var dir = Number(btn.dataset.dir);
      var qty = Number(card.dataset.qty) + dir;
      if (qty < 0) qty = 0;
      if (qty > pickCount) qty = pickCount;
      var others = totalSelected(list) - Number(card.dataset.qty);
      if (others + qty > pickCount) qty = pickCount - others;
      card.dataset.qty = qty;
      card.querySelector('.bogo-qty__val').textContent = qty;
      recalc(list, popup, pickCount);
    };

    recalc(list, popup, pickCount);
    openPopup(popup);
  }

  function totalSelected(list) {
    return Array.prototype.reduce.call(list.querySelectorAll('.bogo-gift-card'),
      function (sum, card) { return sum + Number(card.dataset.qty); }, 0);
  }

  function recalc(list, popup, pickCount) {
    var total = totalSelected(list);
    popup.querySelector('.bogo-picked').textContent = total;
    list.querySelectorAll('.bogo-gift-card').forEach(function (card) {
      var qty = Number(card.dataset.qty);
      card.classList.toggle('is-selected', qty > 0);
      var minus = card.querySelector('[data-dir="-1"]');
      var plus = card.querySelector('[data-dir="1"]');
      if (minus) minus.disabled = qty <= 0;
      if (plus) plus.disabled = total >= pickCount || qty >= pickCount;
    });
    popup.querySelector('.bogo-confirm-btn').disabled = total !== pickCount;
  }

  function openPopup(popup) {
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }
  function closePopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }
  // User explicitly declined -> remember, so we don't nag until they qualify for MORE.
  function declinePopup() {
    var popup = getPopup();
    if (popup && popup.dataset.campaignId) {
      setDismissed(popup.dataset.campaignId, Number(popup.dataset.target || 0));
    }
    closePopup();
  }

  // --------------------------------------------------------------------------
  // Confirm -> add chosen gifts (with quantities)
  // --------------------------------------------------------------------------
  function confirmAdd() {
    var popup = getPopup();
    var campaign = CAMPAIGNS.find(function (c) { return c.id === popup.dataset.campaignId; });
    if (!campaign) return;

    var items = [];
    popup.querySelectorAll('.bogo-gift-card').forEach(function (card) {
      var qty = Number(card.dataset.qty);
      if (qty > 0) items.push({
        id: Number(card.dataset.variant), quantity: qty,
        properties: { _bogo_campaign: campaign.id, _bogo_free: 'true' }
      });
    });
    if (items.length === 0) return;

    var btn = popup.querySelector('.bogo-confirm-btn');
    btn.disabled = true; btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.textContent = 'Add to cart';
        closePopup();
        showToast(campaign.title || 'Your free gift was added!');
        notifyCartChanged();
        verifyDiscount(campaign);
      })
      .catch(function () {
        btn.textContent = 'Add to cart'; btn.disabled = false;
        alert('Sorry — something went wrong adding your gift. Please try again.');
      });
  }

  // --------------------------------------------------------------------------
  // Reconcile the cart with what's owed (covers all quantity cases)
  // --------------------------------------------------------------------------
  function syncAllCampaigns() {
    if (_syncing) return Promise.resolve();
    _syncing = true;
    return getCart().then(function (cart) {
      var chain = Promise.resolve();
      CAMPAIGNS.forEach(function (campaign) {
        chain = chain.then(function () {
          var target = targetGiftQty(cart, campaign);
          var current = giftQtyInCart(cart, campaign);

          if (target === 0) setDismissed(campaign.id, -1);

          if (current > target) {
            return removeGiftUnits(cart, campaign, current - target).then(function () {
              showToast('Your free gift was updated to match your cart.', true);
              notifyCartChanged();
            });
          }

          var remaining = target - current;
          if (remaining > 0 && getPopup() && getPopup().hidden &&
              getDismissed(campaign.id) < target) {
            return fetchGiftProducts(campaign).then(function (gifts) {
              renderPopup(campaign, gifts, remaining, target);
            });
          }
        });
      });
      return chain;
    }).finally(function () { _syncing = false; });
  }

  // Remove N free units for a campaign, trimming gift lines from the end.
  function removeGiftUnits(cart, campaign, units) {
    var lines = giftLinesFor(cart, campaign).slice().reverse();
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        if (units <= 0) return;
        var take = Math.min(units, line.quantity);
        units -= take;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity - take })
        });
      });
    });
    return chain;
  }

  function verifyDiscount(campaign) {
    getCart().then(function (cart) {
      var notZeroed = giftLinesFor(cart, campaign).some(function (line) {
        return (line.final_line_price || line.line_price || 0) > 0;
      });
      if (notZeroed) {
        console.warn('[BOGO] Gift for "' + campaign.id + '" is NOT discounted. ' +
          'Check the automatic "Buy X Get Y" discount is ACTIVE (not expired), its ' +
          'products/quantities match this config, and its "maximum uses per order" ' +
          'matches your "repeat" setting.');
      }
    });
  }

  // --------------------------------------------------------------------------
  // Toast
  // --------------------------------------------------------------------------
  var _toastTimer;
  function showToast(msg, isRemoval) {
    var toast = document.getElementById('bogo-toast');
    if (!toast) return;
    toast.querySelector('.bogo-toast__title').textContent = isRemoval ? 'Cart updated' : 'Free gift added!';
    toast.querySelector('.bogo-toast__msg').textContent = msg;
    toast.hidden = false;
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(hideToast, 5000);
  }
  function hideToast() {
    var toast = document.getElementById('bogo-toast');
    if (!toast) return;
    toast.classList.remove('is-visible');
    setTimeout(function () { toast.hidden = true; }, 300);
  }

  // --------------------------------------------------------------------------
  // Plumbing
  // --------------------------------------------------------------------------
  function notifyCartChanged() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    if (typeof window.openCartDrawer === 'function') { try { window.openCartDrawer(); } catch (e) {} }
  }

  function hookFetch() {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0] && arguments[0].url ? arguments[0].url : arguments[0];
      var p = origFetch.apply(this, arguments);
      if (typeof url === 'string' && /\/cart\/(add|change|update)(\.js)?/.test(url) && !_syncing) {
        p.then(function () { setTimeout(syncAllCampaigns, 60); }).catch(function () {});
      }
      return p;
    };
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // --------------------------------------------------------------------------
  // Wire-up (cart-triggered only)
  // --------------------------------------------------------------------------
  function init() {
    var popup = getPopup();
    if (popup) {
      popup.querySelectorAll('[data-bogo-close]').forEach(function (el) {
        el.addEventListener('click', declinePopup);
      });
      var confirm = popup.querySelector('.bogo-confirm-btn');
      if (confirm) confirm.addEventListener('click', confirmAdd);
    }
    var toastClose = document.querySelector('#bogo-toast .bogo-toast__close');
    if (toastClose) toastClose.addEventListener('click', hideToast);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') declinePopup(); });

    hookFetch();
    syncAllCampaigns();

    document.addEventListener('cart:updated', function () { if (!_syncing) syncAllCampaigns(); });
    document.addEventListener('cart:refresh', function () { if (!_syncing) syncAllCampaigns(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  window.BOGO = { sync: syncAllCampaigns };
})();